import { FC, useEffect, useState } from 'react';
import Select, { GroupBase, OptionsOrGroups, StylesConfig } from 'react-select';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  useAddThinkerMutation,
  useGetMentorsQuery,
  useGetTracksQuery,
} from '../services/apiSlice';

import { AddThinkerFormData, SelectOption, emptyFunction } from '../types';
import { ROLES, emailPattern } from '../constants';
import { hasMailtoPrefix, normalizeEmail } from '../utils/email';
import {
  TrackSelectOption,
  buildTrackSelectOptions,
  findTrackOption,
  formatTrackOptionLabel,
} from '../utils/trackOptions';
import {
  MentorSelectOption,
  buildMentorSelectOptions,
  findMentorOption,
  formatMentorOptionLabel,
} from '../utils/mentorOptions';
import { useAppSelector } from '../stores/AppStore';
import { selectHasOneOfTheRoles } from '../features/user/userSlice';

interface Properties {
  onSuccess: typeof emptyFunction;
}

const AddThinkerForm: FC<Properties> = ({ onSuccess }) => {
  const isOwnerOrSysadmin = useAppSelector((state) =>
    selectHasOneOfTheRoles(state, [ROLES.owner, ROLES.sysadmin]),
  );
  const [trackOptions, setTrackOptions] =
    useState<
      OptionsOrGroups<TrackSelectOption, GroupBase<TrackSelectOption>>
    >();
  const [mentorOptions, setMentorOptions] = useState<MentorSelectOption[]>();
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<AddThinkerFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      track: '',
      trackSerialNum: '',
      mentor: '',
    },
  });
  const { data: tracks, isFetching: isFetchingTracks } = useGetTracksQuery();
  const { data: mentors, isFetching: isFetchingMentors } = useGetMentorsQuery();
  const [addThinker, { isLoading }] = useAddThinkerMutation();
  const [mailtoWarning, setMailtoWarning] = useState<string | undefined>(
    undefined,
  );

  const scheduleFrequencyOptions = Array.from({ length: 5 }, (_, index) => ({
    value: index + 1,
    label: `${index + 1} per week`,
  }));

  useEffect(() => {
    if (tracks) {
      setTrackOptions(() => buildTrackSelectOptions(tracks, isOwnerOrSysadmin));
    }
  }, [tracks, isOwnerOrSysadmin]);

  useEffect(() => {
    if (mentors) {
      setMentorOptions(() => buildMentorSelectOptions(mentors));
    }
  }, [mentors]);

  const formSelectClasses =
    'text-left w-full -m-1 text-slate-500 border-slate-500';

  const formSelectStyles: StylesConfig = {
    control: (baseStyles) => ({
      ...baseStyles,
      background: 'white',
      boxShadow: 'none',
      borderWidth: 1,
      borderColor: 'rgba(229, 231, 235, 1)',
      '&:hover': {
        borderColor: 'rgba(229, 231, 235, 1)',
        cursor: 'pointer',
      },
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      color: 'rgb(100, 116, 139)',
    }),
    groupHeading: (baseStyles) => ({
      ...baseStyles,
      fontSize: '12px',
      fontWeight: 700,
      textTransform: 'uppercase',
      color: 'rgb(71, 85, 105)',
      backgroundColor: 'rgb(241, 245, 249)',
      padding: '6px 12px',
      marginBottom: '4px',
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      color: 'lightgrey',
      opacity: 0.7,
      '&:hover': {
        color: 'grey',
        cursor: 'pointer',
      },
    }),
  };

  const handleAddThinker = async (formData: AddThinkerFormData) => {
    const sanitizedEmail = hasMailtoPrefix(formData.email)
      ? normalizeEmail(formData.email)
      : formData.email;
    if (sanitizedEmail === formData.email) {
      setMailtoWarning(undefined);
    } else {
      setMailtoWarning(
        "It looks like the email was copied with a 'mailto:' prefix. We've removed it—please check that the address is correct.",
      );
    }

    try {
      const trackSerialNumber = tracks?.find(
        (track) => track._id === formData.track,
      )?.serialNum;
      await addThinker({
        ...formData,
        email: sanitizedEmail,
        trackSerialNum: trackSerialNumber ?? '',
      }).unwrap();
    } catch {
      toast.error(
        'Some unexpected error happened while trying to add the thinker. Please try again later!',
      );
      return;
    }
    onSuccess();
  };

  return (
    <div className="z-20 w-full">
      <div className="text-sm text-slate-500 break-words mb-4">
        This form allows you to add a new <strong>Individual Thinker</strong>.
        <br />
        When you submit the form, the system will send a welcome email with a
        login link.
        <br />
        Do <strong>not</strong> try to use this for adding <em>Students</em> to
        a <em>Class</em>.
      </div>
      <div className="grid grid-flow-row bg-gray-100 rounded-xl p-4">
        <form
          onSubmit={handleSubmit(handleAddThinker)}
          className="grid w-full gap-2"
        >
          <div className="font-medium">First name</div>
          <input
            className="w-full p-2 border rounded-md"
            {...register('firstName', {
              required: true,
              minLength: 2,
              maxLength: 50,
            })}
            placeholder="First Name"
          />
          {errors.firstName && (
            <p className="text-sm text-rose-600">
              Please enter a valid first name.
            </p>
          )}
          <div className="font-medium">Last name</div>
          <input
            className="w-full p-2 border rounded-md"
            {...register('lastName', {
              required: true,
              minLength: 2,
              maxLength: 50,
            })}
            placeholder="Last Name"
          />
          {errors.lastName && (
            <p className="text-sm text-rose-600">
              Please enter a valid last name.
            </p>
          )}
          <div className="font-medium">Email</div>
          <input
            className="w-full p-2 border rounded-md"
            {...register('email', {
              required: true,
              pattern: emailPattern,
            })}
            placeholder="Email Address"
          />
          {mailtoWarning && (
            <p className="text-sm text-amber-600">{mailtoWarning}</p>
          )}
          {errors.email && (
            <p className="text-sm text-rose-600">
              Please enter a valid email address.
            </p>
          )}
          <div className="font-medium">Track</div>
          <Controller
            name="track"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                data-type="select"
                placeholder={'Select a track!'}
                isLoading={isFetchingTracks}
                options={trackOptions}
                formatOptionLabel={formatTrackOptionLabel}
                onChange={(selectedOption) =>
                  field.onChange((selectedOption as TrackSelectOption)?.value)
                }
                value={findTrackOption(trackOptions ?? [], field.value)}
                className={formSelectClasses}
                components={{
                  // eslint-disable-next-line unicorn/no-null
                  IndicatorSeparator: () => null,
                }}
                styles={
                  formSelectStyles as StylesConfig<
                    TrackSelectOption,
                    boolean,
                    GroupBase<TrackSelectOption>
                  >
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          />
          {errors.track && (
            <p className="text-sm text-rose-600">Please select a track.</p>
          )}
          <div className="font-medium">Mentor</div>
          <Controller
            name="mentor"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                data-type="select"
                placeholder={'Select a mentor!'}
                isLoading={isFetchingMentors}
                options={mentorOptions}
                formatOptionLabel={(option) => formatMentorOptionLabel(option)}
                onChange={(selectedOption) =>
                  field.onChange((selectedOption as MentorSelectOption)?.value)
                }
                value={findMentorOption(mentorOptions ?? [], field.value)}
                className={formSelectClasses}
                components={{
                  // eslint-disable-next-line unicorn/no-null
                  IndicatorSeparator: () => null,
                }}
                styles={
                  formSelectStyles as StylesConfig<
                    MentorSelectOption,
                    boolean,
                    GroupBase<MentorSelectOption>
                  >
                }
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          />
          {errors.mentor && (
            <p className="text-sm text-rose-600">Please select a mentor.</p>
          )}
          <div className="font-medium">Schedule frequency</div>
          <Controller
            name="scheduleFrequency"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                {...field}
                data-type="select"
                placeholder={'Select the frequency of the quizzes!'}
                options={scheduleFrequencyOptions}
                onChange={(selectedOption) =>
                  field.onChange((selectedOption as SelectOption)?.value)
                }
                value={scheduleFrequencyOptions.find(
                  (option) => option.value === field.value,
                )}
                className={formSelectClasses}
                components={{
                  // eslint-disable-next-line unicorn/no-null
                  IndicatorSeparator: () => null,
                }}
                styles={formSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          />
          {errors.scheduleFrequency && (
            <p className="text-sm text-rose-600">
              Please select the schedule frequency.
            </p>
          )}
          <button
            type="submit"
            className="justify-self-center max-w-[90%] mt-6 py-2.5 px-2 bg-tttDefault text-white font-bold text-xs uppercase rounded shadow-md hover:bg-blue-600 hover:shadow-lg disabled:opacity-50"
            disabled={isLoading}
          >
            Add Individual Thinker
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddThinkerForm;
